import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { MAP_AREAS, getAreaName, type AreaId, type MapArea, type Zone } from "../../content/map";
import { AnimatedAreaIcon } from "./AnimatedAreaIcon";
import { useDressupStore } from "../../store/dressup";

// 和色: 朱（プロローグ）・紺（到着）・常磐色（生活）
const ZONE_BG: Record<Zone, string> = {
  prologue: "#B83018",   // 朱
  arrival: "#1E3868",   // 紺
  life: "#286038",   // 常磐色
};
const ZONE_BORDER: Record<Zone, string> = {
  prologue: "#781808",   // 深朱
  arrival: "#101E48",   // 深紺
  life: "#183820",   // 深常磐
};
const ZONE_HIGHLIGHT: Record<Zone, string> = {
  prologue: "#DE5040",   // 朱の明るい面
  arrival: "#3860A8",   // 紺の明るい面
  life: "#488850",   // 常磐の明るい面
};

/**
 * 漆塗り＆どうぶつワールド風の丸型ステージノード。
 * 動くアイコン(AnimatedAreaIcon) + 動物住民のミニスプライト付き。
 */
export function AreaNode({
  area,
  locked,
  isImmediateNextLocked = false,
  unlockCondition,
  active,
  progress,
  progressByArea,
  onTap,
}: {
  area: MapArea;
  locked: boolean;
  isImmediateNextLocked?: boolean;
  unlockCondition?: { type: string; area: string } | null;
  active: boolean;
  progress?: { done: number; total: number; cleared: boolean };
  progressByArea?: Partial<Record<AreaId, { done: number; total: number; cleared: boolean }>>;
  onTap: (a: MapArea) => void;
}) {
  const { t, i18n } = useTranslation(["common", "learner"]);
  const isJa = i18n.language === "ja";
  const store = useDressupStore();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const cleared = progress?.cleared ?? false;
  const bg = locked ? "#6B6058" : ZONE_BG[area.zone];
  const border = locked ? "#4A3F38" : ZONE_BORDER[area.zone];
  const highlight = locked ? "#8C8078" : ZONE_HIGHLIGHT[area.zone];
  const stageNum = String(area.unlockOrder + 1).padStart(2, "0");
  const nodeTag = area.unlockOrder === 0 ? "START" : area.id === "workplace" ? "GOAL" : active ? "NEXT" : null;

  // 解放条件エリアと残り必要ユニット数の算出
  const targetAreaId = unlockCondition?.type === "area_clear" ? unlockCondition.area : undefined;
  const prevArea = targetAreaId
    ? MAP_AREAS.find((a) => a.id === targetAreaId)
    : MAP_AREAS.find((a) => a.unlockOrder === area.unlockOrder - 1);
  const prevProgress = prevArea && progressByArea ? progressByArea[prevArea.id] : undefined;
  const remainingUnits = prevProgress ? Math.max(1, (prevProgress.total ?? 5) - (prevProgress.done ?? 0)) : 5;
  const prevAreaName = prevArea
    ? getAreaName(prevArea, i18n.language)
    : (isJa ? "前のエリア" : i18n.language === "id" ? "area sebelumnya" : "previous area");

  function handleClick() {
    if (locked) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
    } else {
      onTap(area);
    }
  }

  function handleResidentTap(e: React.MouseEvent) {
    e.stopPropagation();
    setShowGreeting((prev) => !prev);
    setTimeout(() => setShowGreeting(false), 3500);
  }

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      // ビューポートの setPointerCapture に奪われないよう伝播を止める
      onPointerDown={(e) => e.stopPropagation()}
      whileHover={locked ? { scale: 1.02 } : { scale: 1.08, y: -4 }}
      whileTap={locked ? { scale: 0.98 } : { scale: 0.94 }}
      onMouseEnter={() => locked && setShowTooltip(true)}
      onMouseLeave={() => locked && setShowTooltip(false)}
      className="absolute flex flex-col items-center gap-1.5 focus:outline-none select-none"
      style={{
        left: area.position.x - 85,
        top: area.position.y - 56,
        width: 170,
      }}
    >
      {/* 解放条件ツールチップ */}
      <AnimatePresence>
        {showTooltip && locked && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            className="absolute -top-16 z-30 bg-[#2D221C]/95 text-[#FFF8EC] border border-[#d9b45f]/40 px-3 py-1.5 rounded-xl shadow-xl text-[10px] sm:text-xs font-black text-center min-w-[170px] pointer-events-none"
            style={{
              boxShadow: "0 6px 16px rgba(40,15,5,0.3)",
            }}
          >
            {/* 吹き出しの三角 */}
            <div className="absolute bottom-[-5px] left-1/2 -translate-x-1/2 w-0 h-0 border-t-[5px] border-t-[#2D221C]/95 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent" />
            <div className="text-stone-200">
              {isImmediateNextLocked
                ? t("learner:unlock_requirement", {
                  prevArea: prevAreaName,
                  n: remainingUnits,
                })
                : t("learner:unlock_further", {
                  prevArea: prevAreaName,
                })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 住民タップ時の大阪弁挨拶吹き出し */}
      <AnimatePresence>
        {showGreeting && !locked && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="absolute -top-16 -right-6 z-40 bg-[#fffdf7] text-[#273f7a] border-2 border-[#d9b45f] px-3 py-1.5 rounded-2xl shadow-xl text-[11px] font-black text-left min-w-[150px] max-w-[200px] pointer-events-none"
            style={{ boxShadow: "0 8px 20px rgba(60,20,0,0.25)" }}
          >
            <div className="text-[9px] text-[#8f332c] font-black">{area.residentNameJa}</div>
            <div className="leading-snug mt-0.5">
              {isJa ? area.residentGreetingJa : area.residentGreetingEn}
            </div>
            <div className="absolute bottom-[-6px] right-6 w-0 h-0 border-t-[6px] border-t-[#d9b45f] border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ロック時のバッジ（直後の1ノードのみ残ユニット数、以遠は Stage 番号のみで誤情報防止） */}
      {locked && (
        <span
          className="absolute -top-7 rounded-full px-2.5 py-0.5 text-[10px] font-black tracking-wide shadow-md z-20 bg-stone-800/90 text-amber-300 border border-amber-400/40"
        >
          {isImmediateNextLocked
            ? `🔒 ${t("learner:unlock_remaining", { n: remainingUnits })}`
            : `🔒 Stage ${stageNum}`}
        </span>
      )}

      {/* START / NEXT / GOAL バッジ */}
      {nodeTag && !showTooltip && !locked && (
        <motion.span
          initial={false}
          animate={active ? { y: [0, -4, 0] } : {}}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-8 rounded-full px-3 py-1 text-[11px] font-black tracking-wide shadow-md z-20"
          style={{
            background: nodeTag === "GOAL" ? "#C8940A" : nodeTag === "START" ? "#8A2018" : "#F5EFE0",
            color: nodeTag === "NEXT" ? "#8A2018" : "#FFF8E8",
            border: nodeTag === "NEXT" ? "2px solid #C8940A" : "2px solid rgba(255,248,232,0.8)",
          }}
        >
          {nodeTag}
        </motion.span>
      )}

      {/* ── どうぶつ住民のミニスプライト（ノード右上にちょこんと座る、タップ可能） ── */}
      {!locked && (
        <motion.div
          onClick={handleResidentTap}
          whileTap={{ scale: 0.85 }}
          animate={{
            y: [0, -3, 0],
            rotate: [-3, 3, -3],
          }}
          transition={{
            repeat: Infinity,
            duration: 2.8,
            delay: (area.unlockOrder % 4) * 0.4,
            ease: "easeInOut",
          }}
          className="absolute -top-3 -right-1 z-20 w-8 h-8 rounded-full bg-[#fffdf7] border-2 border-[#d9b45f] shadow-md flex items-center justify-center text-base cursor-pointer hover:scale-110 transition-transform"
          title={`${area.residentNameJa} (${area.residentRoleJa})`}
        >
          <span>{area.residentEmoji}</span>
        </motion.div>
      )}

      {/* ── プレイヤーの着せ替えモデル駒（現在地ノードの左上に座る） ── */}
      {active && (
        <motion.div
          animate={{
            y: [0, -5, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            repeat: Infinity,
            duration: 2,
            ease: "easeInOut",
          }}
          className="absolute -top-4 -left-2 z-30 w-9 h-9 rounded-full bg-[#fffdf7] border-2 border-[#8f332c] shadow-lg flex items-center justify-center text-lg pointer-events-none"
          title="あなたの着せ替えアバター現在地"
        >
          <span>
            {store.animalType === "frog"
              ? "🐸"
              : store.animalType === "tanuki"
                ? "🍃"
                : store.animalType === "kitsune"
                  ? "🦊"
                  : store.animalType === "monkey"
                    ? "🐒"
                    : "🐰"}
          </span>
          <span className="absolute -bottom-1 bg-[#8f332c] text-white text-[8px] font-black px-1 rounded-full shadow-xs">
            YOU
          </span>
        </motion.div>
      )}

      {/* ── 外縁金輪（金泥の縁取り） ── */}
      <div
        className="rounded-full relative"
        style={{
          width: 112,
          height: 112,
          padding: 5,
          background: locked
            ? "rgba(120,100,80,0.18)"
            : `radial-gradient(circle at 40% 30%, rgba(255,220,100,0.55), rgba(160,110,0,0.30))`,
          boxShadow: active
            ? "0 0 0 5px rgba(200,148,10,0.65), 0 10px 28px rgba(60,20,0,0.28)"
            : locked
              ? "0 4px 12px rgba(0,0,0,0.15)"
              : "0 8px 22px rgba(60,20,0,0.22)",
        }}
      >
        {/* ── 漆塗り本体 ── */}
        <div
          className="relative w-full h-full rounded-full border-[5px] flex flex-col items-center justify-center overflow-hidden"
          style={{
            background: `radial-gradient(circle at 36% 32%, ${highlight} 0%, ${bg} 65%)`,
            borderColor: border,
          }}
        >
          {locked ? (
            <>
              {/* 霧の向こうのシルエット */}
              <div className="opacity-[0.25] blur-[1px] absolute">
                <AnimatedAreaIcon areaId={area.id} size={42} still />
              </div>
              {/* 霧（フォグ）エフェクト */}
              <div className="absolute inset-0 bg-white/10 pointer-events-none">
                <div className="absolute inset-0 bg-white/20 blur-[5px]" />
                <div className="absolute -inset-4 bg-stone-400/10 blur-[8px]" />
              </div>
              <LockIcon />
            </>
          ) : (
            <div className="relative flex flex-col items-center justify-center">
              {/* 動くシンボルアイコン */}
              <div className="mb-0.5">
                <AnimatedAreaIcon areaId={area.id} size={38} />
              </div>
              {/* ステージ番号またはクリアマーク */}
              {cleared ? (
                <div className="absolute bottom-1 bg-emerald-600/90 rounded-full px-1.5 py-0.2 shadow">
                  <ClearIcon />
                </div>
              ) : (
                <span
                  className="font-black text-white/95 leading-none select-none text-xs"
                  style={{
                    textShadow: "0 1px 3px rgba(0,0,0,0.6)",
                  }}
                >
                  ST.{stageNum}
                </span>
              )}
            </div>
          )}

          {/* アクティブ時: 金色パルスリング */}
          {active && (
            <motion.span
              className="pointer-events-none absolute inset-0 rounded-full border-[3px]"
              style={{ borderColor: "#C8940A" }}
              animate={{ scale: [1, 1.5], opacity: [0.9, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
            />
          )}
        </div>
      </div>

      {/* ── ステージ名（墨文字） ── */}
      <span
        className="font-black text-center leading-none px-1 tracking-normal"
        style={{
          color: locked ? "#7A706A" : "#2A1808",
          fontSize: 20,
          textShadow: "0 2px 0 rgba(255,248,232,0.82), 0 4px 12px rgba(60,20,0,0.18)",
        }}
      >
        {area.nameJa}
      </span>
    </motion.button>
  );
}

function ClearIcon() {
  return (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width={28} height={28} fill="rgba(255,240,220,0.65)">
      <path d="M12 1.5a4.5 4.5 0 0 0-4.5 4.5V9H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1.5V6A4.5 4.5 0 0 0 12 1.5zm-2.5 4.5a2.5 2.5 0 0 1 5 0V9h-5V6z" />
    </svg>
  );
}
